import { PropsWithChildren, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClientPoint,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { useCalendarView, useCalendarViewProps } from './providers';
import { TimeTooltip } from './time-tooltip';
import { getAdjustedOneHourInMinutes, getDurationFromHours, getTimestamp } from './utils';
import { useZoomSafeContext } from './zoom/zoom-context';
import { checkIsHalfHourMarkersVisible } from './zoom/zoom-context';

const shouldHideTooltip = (event?: Event): boolean => {
  if (!event) return false;

  // Get the composed path, fallback to target if composedPath is empty
  const path = event.composedPath?.() || [];

  // If composedPath is empty or not available, use closest() method
  if (path.length === 0 && event.target) {
    const target = event.target as Element;
    // Use closest() to check if target or any parent has the attribute
    return target.closest?.('[data-hide-calendar-tooltip="true"]') !== null;
  }

  // Use the normal composedPath approach
  return path.some((el) => {
    return (el as Element).getAttribute?.('data-hide-calendar-tooltip') === 'true';
  });
};

type CalendarViewContainerProps = PropsWithChildren<{
  numberOfHours: number;
  hasProviders?: boolean;
}>;

export const CalendarViewContainer = ({ children, numberOfHours }: CalendarViewContainerProps) => {
  const { pixelsPerMinute } = useZoomSafeContext();
  const shouldShowHalfHourMarkers = checkIsHalfHourMarkersVisible(pixelsPerMinute);
  const hoveredTimestamp = useRef('0');
  const [isOpen, setIsOpen] = useState(false);

  const lastMousePosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const { startHour, endHour } = useCalendarViewProps();
  const { backgroundView, setHoveredTimeProgressPosition, floatingTooltipData } = useCalendarView([
    'backgroundView',
    'setHoveredTimeProgressPosition',
    'floatingTooltipData',
  ]);
  const { allRequestedOpenings, isTruncated, size } = floatingTooltipData ?? {};

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !backgroundView,
    placement: 'top',
    middleware: [offset(20), flip(), shift()],
    onOpenChange: (open, event) => {
      // Only prevent opening if we're trying to open and should hide tooltip
      if (open && shouldHideTooltip(event)) return;

      setIsOpen(open);
    },
    whileElementsMounted: autoUpdate,
  });

  const clientPoint = useClientPoint(context);

  const hover = useHover(context, {
    restMs: 500,
    move: false,
    delay: {
      open: 500,
      close: 200,
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([clientPoint, hover]);

  return (
    <>
      <section
        ref={refs.setReference}
        {...getReferenceProps({
          onMouseDown: () => setIsOpen(false),
          onMouseMove: (event) => {
            // Hide tooltip when hovering over elements with data-hide-calendar-tooltip attribute
            if (shouldHideTooltip(event.nativeEvent)) {
              setIsOpen(false);
              return;
            }

            // Close tooltip on mouse movement
            const currentX = event.clientX;
            const currentY = event.clientY;
            const closeThreshold = 5; // pixels

            if (
              Math.abs(currentX - lastMousePosition.current.x) > closeThreshold ||
              Math.abs(currentY - lastMousePosition.current.y) > closeThreshold
            ) {
              if (isOpen) {
                setIsOpen(false);
              }
            }

            lastMousePosition.current = { x: currentX, y: currentY };

            const timestamp = getTimestamp({
              event,
              element: refs.domReference.current,
              startHour,
              endHour,
              pixelsPerMinute,
            });

            if (!timestamp) return;

            const { formattedTime, totalMinutes } = timestamp;

            setHoveredTimeProgressPosition(totalMinutes);
            hoveredTimestamp.current = formattedTime;
          },
        })}
        css={containerStyle(numberOfHours, pixelsPerMinute, shouldShowHalfHourMarkers)}
      >
        {children}
      </section>
      <AnimatePresence>
        {isOpen && !backgroundView ? (
          <motion.div
            ref={refs.setFloating}
            layout={false}
            style={floatingStyles}
            {...getFloatingProps()}
            css={{ pointerEvents: 'none', zIndex: theme.zIndex.hints }}
          >
            <TimeTooltip>
              {floatingTooltipData ? (
                <div css={{ display: 'flex', flexDirection: 'column' }}>
                  <TextRow weight='bold' isTruncated={isTruncated} size={'medium'}>
                    {floatingTooltipData.name}
                  </TextRow>
                  {floatingTooltipData.personName && (
                    <TextRow isTruncated={isTruncated} size={size}>
                      {floatingTooltipData.personName}
                    </TextRow>
                  )}
                  <TextRow isTruncated={isTruncated} size={size}>
                    {floatingTooltipData.appointmentType ?? ''}
                  </TextRow>
                  <TextRow isTruncated={isTruncated} size={size}>
                    {floatingTooltipData.value}
                  </TextRow>
                  {allRequestedOpenings &&
                    allRequestedOpenings.map((opening, index) => {
                      const duration = getDurationFromHours(opening.startTime, opening.endTime);
                      const dayString = dayjs(opening.startDate).format('MMM D');
                      return (
                        <TextRow key={index} isTruncated={isTruncated} size={size}>
                          {`${dayString} ${duration}`}
                        </TextRow>
                      );
                    })}
                </div>
              ) : (
                hoveredTimestamp.current
              )}
            </TimeTooltip>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

const TextRow = ({
  children,
  size,
  isTruncated = true,
  weight,
}: {
  children: React.ReactNode;
  size?: 'small' | 'medium';
  isTruncated?: boolean;
  weight?: 'regular' | 'bold';
}) => {
  return (
    <>
      {isTruncated ? (
        <Truncated as={Text} size={size ?? 'small'} weight={weight} color='white'>
          {children}
        </Truncated>
      ) : (
        <Text size={size ?? 'small'} weight={weight} color='white'>
          {children}
        </Text>
      )}
    </>
  );
};

const containerStyle = (numberOfRows: number, pixelsPerMinute: number, shouldShowHalfHourMarkers: boolean) => {
  const rowHeight = getAdjustedOneHourInMinutes(pixelsPerMinute) / (shouldShowHalfHourMarkers ? 2 : 1);

  return css({
    display: 'grid',
    position: 'relative',
    backgroundColor: theme.colors.neutral5,
    marginTop: theme.spacing(5),
    gridTemplateRows: `repeat(${numberOfRows}, ${rowHeight}px)`,
    gridTemplateColumns: 'auto auto 1fr',
    width: '100%',
  });
};
